/**
 * Groups "profile", "session", "time", "progress", "tutorial", "settings" and "names" of the progress ops: creating
 * and restarting a profile, the session entry and time refresh, the tutorial flag, the settings screen and worm names.
 *
 * The session entry follows FWP.net_load_profile (FWP.ts:2251-2285): apply_state's load normalizations (2316-2335) and
 * net_initialized's level-from-exp (2352) - both in ../state.ts normalizeLoaded -, net_refresh_time (modify_tired /
 * modify_papa / modify_wins, ./battle-time.ts) and modify_last_enter (4775-4822). Every day and hour comes from ctx.calendar (the player's stored time zone),
 * not from the host's time zone as in the original.
 */
import { argsObject, boolArg, optionalIntArg } from '../args';
import { int, keys } from '../as3';
import { MAX_TZ_OFFSET_MIN, MIN_TZ_OFFSET_MIN, type RuleContext } from '../context';
import { playerLevel, type GameDefs } from '../defs';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import { GAME_STATE_FORMAT, normalizeLoaded, type PlayerState, type StoredGameState, type WorkingState } from '../state';
import { refreshTime } from './battle-time';
import { dayOf, isNextDay, sameDay, syncLevel } from './progress-common';

/** The stored state of an account that has no profile yet: the input of profile.create. */
export function emptyGameState(): StoredGameState {
  return { format: GAME_STATE_FORMAT, player: {}, map_info: [] };
}

/** FWP.player before any profile exists (the field initializer, FWP.ts:187-202): create_new_params fills it in place. */
function playerTemplate(): PlayerState {
  return {
    level: 1,
    perks: {},
    stim_behave: {},
    stim_to_fight: {},
    weapons: {},
    settings: {},
    achiev: {},
    last_achiev: {},
    progress: {},
    quick_weapons: {},
    flags: {},
    rip: {},
    impl: {},
    objs: {},
    recipes: [],
  } as unknown as PlayerState;
}

/**
 * FWP.create_new_params(mode) for the player object (FWP.ts:4512-4705), in place and in the same assignment order, so
 * the key order of the stored player matches the game's. 'null' (start over) keeps view_tutorial and every field and
 * achievement key the function does not assign. `dev` is the developer profile branch (DEV_PROFILE, 4675-4704).
 */
export function createNewParams(p: PlayerState, mode: 'first time' | 'null', defs: GameDefs, dev: boolean): void {
  const q = p as any;
  q.progress = {
    wormville_missions: 0,
    wormville_career: 0,
    mines_missions: 0,
    mines_career: 0,
    alps_missions: 0,
    alps_career: 0,
    dust_career: 0,
    island_missions: 0,
  };
  q.coord_x = 5;
  q.coord_y = 4;
  q.attack = 0;
  q.protect = 0;
  q.health = 0;
  q.worms = 0;
  q.perks = {};
  q.stim_behave = { stimpack: 5, jet: 1, buffout: 1 };
  q.stim_to_fight = {};
  q.spec = 0;
  q.skill_pts = 15;
  q.perk_pts = 10;
  q.gold = 800;
  q.diam = 0;
  q.tired = 0;
  q.last_tired = 0;
  q.last_enter = 0;
  q.tween = 0;
  q.exp = 0;
  q.level = int(playerLevel(defs, q.exp));
  q.weapons = {};
  q.career_max = 1;
  q.career_attempt = 0;
  q.missions_max = 1;
  q.missions_attempt = 0;
  q.survival_max = 1;
  q.survival_attempt = 0;
  q.boss_max = 1;
  q.boss_attempt = 0;
  q.settings = { skill: 0, help: 0, sound: 0, exhaust: 0, no_animation: 0 };
  q.first_time = true;
  q.admin_state = 20;
  q.inc_state = 0;
  q.last_exp = 0;
  const a = q.achiev;
  a['kill_p'] = 0;
  a['kill_e'] = 0;
  a['kill_a'] = 0;
  a['kill_c'] = 0;
  a['kill_h'] = 0;
  a['kill_f'] = 0;
  a['kill_l'] = 0;
  a['career'] = q.career_max - 1;
  a['missions'] = q.missions_max - 1;
  a['survival'] = q.survival_max - 1;
  a['boss'] = q.boss_max - 1;
  a['assault'] = 0;
  a['recipes'] = 0;
  a['upgraded'] = 0;
  a['days'] = 0;
  a['no_death'] = 0;
  a['no_injure'] = 0;
  a['buys'] = 0;
  a['wins'] = 0;
  a['crafted'] = 0;
  a['stims'] = 0;
  a['para'] = 0;
  a['annih'] = 0;
  a['steal'] = 0;
  q.last_achiev = {
    career: 0,
    missions: 0,
    survival: 0,
    boss: 0,
    assault: 0,
    no_death: 0,
    no_injure: 0,
    kill_p: 0,
    kill_e: 0,
    kill_a: 0,
    kill_c: 0,
    kill_h: 0,
    kill_f: 0,
    kill_l: 0,
    para: 0,
    annih: 0,
    recipes: 0,
    stims: 0,
    buys: 0,
    steal: 0,
    crafted: 0,
    upgraded: 0,
    wins: 0,
    days: 0,
  };
  q.last_achiev_range = 0;
  q.achiev_rating_bonus = 0;
  q.perk_slots = 2;
  q.diam_magnet = 0;
  for (const k of ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'k', 'l']) q.quick_weapons[k] = 0;
  q.flags = {};
  q.rip = {};
  q.impl = {};
  q.objs = {};
  q.cur_flag = 0;
  q.cur_rip = 0;
  q.cur_impl = 0;
  if (mode != 'null') q.view_tutorial = 0;
  q.today_steals = 0;
  q.last_steal = 0;
  q.rating = 0;
  q.today_date = 0;
  q.today_wins = 0;
  q.yest_wins = 0;
  q.last_papa = 0;
  q.papa_asked = 0;
  for (let i = 1; i <= 5; i++) q['worm_name' + i] = '';
  q.last_reset = 0;
  q.next_assault_payment = 0;
  q.cur_task = 1;
  q.last_presented_task = 1;
  q.got_cmd_bonus = 0;
  q.recipes[0] = defs.CRAFT_DEF[0];
  for (let i = 1; i <= q.recipes[0]; i++) q.recipes[i] = 0;
  if (dev) {
    q.attack = 199;
    q.protect = 700;
    q.health = 0;
    q.worms = 0;
    q.tired = 0;
    q.skill_pts = 1000;
    q.perk_pts = 3000;
    q.gold = 5000000;
    q.diam = 5000;
    q.spec = 0;
    q.settings.skill = 10;
    q.career_max = defs.TEAMS_DEF.career_max;
    q.missions_max = defs.TEAMS_DEF.missions_max;
    q.survival_max = defs.TEAMS_DEF.survival_max;
    q.boss_max = defs.TEAMS_DEF.boss_max - 2;
    q.tween = 2;
    q.perks = { gsearch: 3 };
    q.objs = { obj_ultra_mod: 7, obj_laser_mod: 7 };
    q.cur_task = 30;
    q.last_presented_task = 1;
    const W = defs.WEAPONS_DEF;
    for (const k of keys(W)) {
      const key = String(k);
      if (key.search('rez') == -1 && W[key].id <= 200 && key != 'annihilator') {
        q.weapons[key] = [3, { range: 1, stolen: false, crafted: false }, { range: 1, stolen: false, crafted: false }, { range: 1, stolen: false, crafted: false }];
      }
    }
    q.exp = int(defs.LEVEL_THRESHOLD[10] - 1);
    q.level = int(playerLevel(defs, q.exp));
    q.perk_slots = 2;
  }
}

// ------------------------------------------------------------------------------------------------ time

// FWP.modify_tired / modify_papa / modify_wins (net_refresh_time) are ported once, in ./battle-time.ts (refreshTime):
// battle.start runs them too.

/**
 * FWP.modify_last_enter (FWP.ts:4775-4822): on a new calendar day the login streak `tween` grows (or restarts at 1),
 * the daily present becomes due (FWP.get_present = tween), achiev.days++ and today_craft_sells = 0; last_enter = now;
 * today_steals = 0 unless the last steal was today. Returns get_present (0 = no new day).
 */
export function modifyLastEnter(p: PlayerState, ctx: RuleContext): number {
  let present = 0;
  const now = dayOf(ctx, ctx.now);
  const last = dayOf(ctx, p.last_enter);
  if (!sameDay(now, last)) {
    if (isNextDay(now, last) || p.last_enter == 0) {
      if (p.last_enter != 0) ++p.tween;
      else p.tween = 1;
    } else {
      p.tween = 1;
    }
    present = p.tween;
    ++p.achiev['days'];
    p.today_craft_sells = 0;
  }
  p.last_enter = ctx.now;
  if (p.last_steal > 0) {
    const steal = dayOf(ctx, p.last_steal);
    if (!sameDay(now, steal)) p.today_steals = 0;
  } else {
    p.today_steals = 0;
  }
  return present;
}

// ------------------------------------------------------------------------------------------------ names

/** The worm name fields of the settings screen (worm_name1..5). */
export const WORM_NAMES = 5;
/** Longest stored worm name (the backend projector cuts names to 15 characters). */
export const MAX_WORM_NAME = 15;
/** Default text of a worm name field (render 3160) - the canonical string the original saved for untouched fields. */
export const DEFAULT_WORM_NAME = 'Червь #';

/**
 * The characters of a worm name (new_interf.check_tf, the settings screen's fields): space, '#', digits, Latin
 * letters and Cyrillic letters. PORT: the original allowed Cyrillic А..я only; the Ukrainian and Belarusian letters
 * (Ё ё І і Ї ї Є є Ґ ґ Ў ў: the Cyrillic block U+0400-U+045F and Ґ ґ) and the apostrophe (' ʼ ’: "Черв'як", the
 * Ukrainian default name) are letters of the game's languages too.
 */
export function isAllowedNameChar(code: number): boolean {
  return (
    code == 32 ||
    code == 35 ||
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    (code >= 0x400 && code <= 0x45f) ||
    code == 0x490 ||
    code == 0x491 ||
    code == 39 ||
    code == 0x2bc ||
    code == 0x2019
  );
}

function checkTzArg(o: Record<string, unknown>): number | undefined {
  return optionalIntArg(o, 'tzOffsetMin', { min: MIN_TZ_OFFSET_MIN, max: MAX_TZ_OFFSET_MIN });
}

// ------------------------------------------------------------------------------------------------ ops

export interface SessionEnterResult {
  /** FWP.PAPA_HERE: Papa John's shop is open this hour. */
  papaHere: boolean;
  /** A new calendar day started (modify_last_enter found another day). */
  newDay: boolean;
  /**
   * The daily present screen is due (new_interf.ts:254-256: get_present != 0 and the tutorial is done): the server
   * stores it as today's present and passes it to present.claim.
   */
  presentAvailable: boolean;
  /** The login streak the present is for (FWP.get_present). */
  present: number;
  /** The offset the calendar was computed in (ctx.tzOffsetMin). */
  tzOffsetMin: number;
}

export interface SettingsSetArgs {
  /** Difficulty 0..4 (skill0..skill4 of the settings screen); keeps the hardcore mode. */
  difficulty?: number;
  /** Target hardcore mode (settings.skill >= 10). */
  hardcore?: boolean;
  /** Target animation mode (settings.no_animation == 0). */
  animation?: boolean;
  /** The device's time zone (minutes east of UTC); stored by the command service, not in the game state. */
  tzOffsetMin?: number;
}

export const profileOps = {
  /**
   * time.refresh: 'refresh' (tired screen) / 'refresh_papa' -> FWP.init_refresh (FWP.ts:4894-4910) ->
   * net_refresh_time: modify_tired, modify_papa, modify_wins with the server time.
   */
  'time.refresh': defineOp<Record<string, never>, { papaHere: boolean; tired: number }>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state, _args, ctx) {
      const p = state.player;
      const { papaHere } = refreshTime(p, ctx);
      return { state, result: { papaHere, tired: p.tired } };
    },
  }),

  /**
   * progress.startOver: 'null_all' -> 'question' -> 'proceed' (new_interf.ts:5664, 6039-6043) ->
   * FWP.create_new_params('null'), saved as a 'reset'. Precondition (render 3185-3188): level >= 5 (derived from exp
   * first). view_tutorial is kept, and so is every field / achievement key create_new_params does not assign.
   * Deviation: a game started with DEV_PROFILE re-created the developer profile here (FWP.ts:4675); a client command
   * cannot ask for that, so start over always gives a normal new profile.
   */
  'progress.startOver': defineOp<Record<string, never>, Record<string, never>>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state, _args, ctx) {
      const p = state.player;
      syncLevel(p, ctx.defs);
      if (!(p.level >= 5)) throw new RuleError('level_too_low', { level: p.level, need: 5 });
      if (!p.achiev || !p.quick_weapons || !Array.isArray(p.recipes)) throw new RuleError('bad_state', { problem: 'player without achiev/quick_weapons/recipes' });
      createNewParams(p, 'null', ctx.defs, false);
      return { state, result: {} };
    },
  }),

  /** tutorial.close: 'close_tutorial' of the automatically opened tutorial (new_interf.ts:5531-5539): view_tutorial = 1. */
  'tutorial.close': defineOp<Record<string, never>, Record<string, never>>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state) {
      const p = state.player;
      // The tutorial opens by itself only while view_tutorial != 1 (new_interf.ts:257-259); opened by the player it
      // closes without a save.
      if (p.view_tutorial == 1) throw new RuleError('not_available', { problem: 'tutorial already done' });
      p.view_tutorial = 1;
      return { state, result: {} };
    },
  }),

  /**
   * settings.set { difficulty?, hardcore?, animation?, tzOffsetMin? } (at least one): the settings screen.
   *  - difficulty N (skill0..skill4, click_proceed('skill', N) new_interf.ts:6252-6260): skill = N (+ 10 in hardcore).
   *    Fixed defect: the original handled every control whose name contains "skill" (new_interf.ts:5637-5639), so a
   *    click on the skill screen's 'skill_attack' text field stored Number('_attack') = NaN; only 0..4 are accepted.
   *  - hardcore (change_hardcore 5640-5649, a toggle of skill +/- 10) and animation (change_animation 5650-5659, a toggle
   *    of no_animation) are target values, so a repeated request cannot flip them back.
   *  - tzOffsetMin: range check only; the command service stores the device offset (see session.enter).
   */
  'settings.set': defineOp<SettingsSetArgs, { skill: number; no_animation: number; tzOffsetMin?: number; changed: boolean }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['difficulty', 'hardcore', 'animation', 'tzOffsetMin']);
      const out: SettingsSetArgs = {};
      const d = optionalIntArg(o, 'difficulty', { min: 0, max: 4 });
      if (d !== undefined) out.difficulty = d;
      if (o.hardcore !== undefined) out.hardcore = boolArg(o, 'hardcore');
      if (o.animation !== undefined) out.animation = boolArg(o, 'animation');
      const tz = checkTzArg(o);
      if (tz !== undefined) out.tzOffsetMin = tz;
      if (Object.keys(out).length === 0) throw new RuleError('bad_args', { field: '', problem: 'nothing to set' });
      return out;
    },
    run(state, args) {
      const s = state.player.settings;
      const before = JSON.stringify([s.skill, s.no_animation]);
      if (args.difficulty !== undefined && s.skill != args.difficulty) {
        let hard = 0;
        if (s.skill >= 10) hard = 10;
        s.skill = args.difficulty + hard;
      }
      if (args.hardcore !== undefined) {
        if (args.hardcore === false && s.skill >= 10) s.skill -= 10;
        else if (args.hardcore === true && s.skill < 10) s.skill += 10;
      }
      if (args.animation !== undefined) s.no_animation = args.animation ? 0 : 1;
      return {
        state,
        result: {
          skill: s.skill,
          no_animation: s.no_animation,
          ...(args.tzOffsetMin !== undefined ? { tzOffsetMin: args.tzOffsetMin } : {}),
          changed: JSON.stringify([s.skill, s.no_animation]) !== before,
        },
      };
    },
  }),

  /**
   * names.set { names }: 'save_names' of the settings screen (new_interf.ts:5352-5361) stores the text of all five
   * worm_name fields. Only the first worms + 2 fields are editable (render 3158-3175); the others show the stored
   * name or the default 'Червь #<n>' and the original saved exactly that text, so the rule does the same for every
   * field the request does not set. Names keep check_tf's characters (isAllowedNameChar) and at most 15 characters.
   */
  'names.set': defineOp<{ names: string[] }, { names: string[] }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['names']);
      if (!Array.isArray(o.names) || o.names.length > WORM_NAMES) throw new RuleError('bad_args', { field: 'names', problem: `must be an array of at most ${WORM_NAMES} names` });
      const names = o.names.map((n, i) => {
        if (typeof n !== 'string') throw new RuleError('bad_args', { field: `names.${i}`, problem: 'must be a string' });
        if (n.length > MAX_WORM_NAME) throw new RuleError('bad_args', { field: `names.${i}`, problem: `must be at most ${MAX_WORM_NAME} characters` });
        for (let c = 0; c < n.length; c++) {
          if (!isAllowedNameChar(n.charCodeAt(c))) throw new RuleError('bad_args', { field: `names.${i}`, problem: 'contains a character the name field does not accept' });
        }
        return n;
      });
      return { names };
    },
    run(state, { names }) {
      const p = state.player as any;
      const editable = p.worms + 2;
      if (names.length > editable) throw new RuleError('not_editable', { names: names.length, editable });
      const out: string[] = [];
      for (let i = 1; i <= WORM_NAMES; i++) {
        const shown = p['worm_name' + i] ? p['worm_name' + i] : DEFAULT_WORM_NAME + i;
        p['worm_name' + i] = i <= names.length ? names[i - 1] : shown;
        out.push(p['worm_name' + i]);
      }
      return { state, result: { names: out } };
    },
  }),
};

/**
 * Ops the server runs itself (SERVER_RULE_OPS): the initial state of a new account and the entry normalizations of
 * a session start (profile.load), with the server-trusted `dev` flag.
 */
export const profileServerOps = {
  /**
   * profile.create { dev? }: the initial state of a new account, FWP.create_new_params('first time') on the empty
   * player (net_load_profile FWP.ts:2270-2272). `dev` = the developer profile branch; the command service passes it
   * only for accounts that may have one. The input must be emptyGameState(). The game then runs the session entry, so
   * the service runs session.enter { created: true } right after (the game's first save contains both).
   */
  'profile.create': defineOp<{ dev: boolean }, { dev: boolean }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['dev']);
      return { dev: o.dev === undefined ? false : boolArg(o, 'dev') };
    },
    run(state, { dev }, ctx) {
      if (Object.keys(state.player).length > 0) throw new RuleError('profile_exists');
      const p = playerTemplate();
      createNewParams(p, 'first time', ctx.defs, dev);
      const out: WorkingState = { player: p, map_info: [], protectedPaths: [], extra: state.extra };
      return { state: out, result: { dev } };
    },
  }),

  /**
   * session.enter { tzOffsetMin?, created? }: the entry normalizations of every session start (see the file comment).
   * Returns the volatile facts the client kept in memory (PAPA_HERE, get_present). `tzOffsetMin` is the device's
   * offset the client reports; the command service decides whether to adopt it (at most one change per 24 h) and
   * builds ctx with the adopted offset - the rule only checks its range and computes in ctx.tzOffsetMin.
   * `created` (server-supplied): the profile was created by profile.create in this very session start; the game ran
   * apply_state's load normalizations only on a loaded state (net_load_profile FWP.ts:2259-2272), so they are skipped
   * (it matters for the developer profile: exp > 0 with rating 0).
   */
  'session.enter': defineOp<{ tzOffsetMin?: number; created?: boolean }, SessionEnterResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['tzOffsetMin', 'created']);
      const tz = checkTzArg(o);
      return { ...(tz === undefined ? {} : { tzOffsetMin: tz }), ...(o.created === undefined ? {} : { created: boolArg(o, 'created') }) };
    },
    run(state, args, ctx) {
      const p = state.player;
      if (Object.keys(p).length === 0) throw new RuleError('no_profile');
      // modify_last_enter increments achiev.days: a player without the object would crash the original.
      if (!p.achiev || typeof p.achiev !== 'object') throw new RuleError('bad_state', { problem: 'player without achiev' });
      // apply_state's normalizations + net_initialized's level (../state.ts normalizeLoaded); a profile made by
      // profile.create in this session start was never loaded, so it only gets the level.
      if (args.created) syncLevel(p, ctx.defs);
      else normalizeLoaded(state, ctx.defs);
      const { papaHere } = refreshTime(p, ctx);
      const present = modifyLastEnter(p, ctx);
      return {
        state,
        result: { papaHere, newDay: present != 0, presentAvailable: present != 0 && p.view_tutorial == 1, present, tzOffsetMin: ctx.tzOffsetMin },
      };
    },
  }),
};
